// src/components/ShareModal.js
import React, { useState , useEffect} from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  makeStyles,
  Button,
  TextField,
  MenuItem,
  Checkbox
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CloseIcon from "@material-ui/icons/Close";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import ShareIcon from "@material-ui/icons/Share";
import DocuwareTemplateService from "../../services/DocuwareTemplateService";
import { CircularProgress } from "@material-ui/core";
import Backdrop from '@material-ui/core/Backdrop';
import notify from "../../utils/notifier";
import authUtil from "../../utils/AuthUtils";
import ManageAccessModal from "./ManageAccessModal";




const useStyles = makeStyles((theme) => ({
  drawerPaper: {
    width: "30%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#F6F6F6",
  },
  titleBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#fff",
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),

  },
  content: {
    flex: 1,
    padding: theme.spacing(2),
  },
  section: {
    marginBottom: theme.spacing(2),
  },
  optionRow: {
    display: "flex",
    alignItems: "center",
    padding: "6px 12px",
  },
  dropdownPaper: {
    backgroundColor: "#fff",
    borderRadius: 8,
    marginTop: theme.spacing(1),
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 12px",
    borderTop: "1px solid #eee",
    background: "#fff",
  },
  countBadge: {
    border: "1px solid #999",
    borderRadius: 4,
    padding: "2px 6px",
    marginLeft: 6,
    fontSize: 12,
  },
  footer1: {
    borderTop: "1px solid #ddd",
    padding: theme.spacing(2),
    display: "flex",
    justifyContent: "flex-end",
    backgroundColor: "#fff",
  },
  shareBtn: {
    borderRadius: "999px",
    padding: theme.spacing(1, 3),
    backgroundColor: "#0E4B5B",
    color: "#fff",
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "#093642",
    },
  },
}));

export default function ShareModal({ open, onClose, document , fileIdentifiers}) {
  const classes = useStyles();
  const [tenants, setTenants] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [backdrop, setBackdrop] = useState(false);
  const [manageAccessOpen, setManageAccessOpen] = useState(false);
  const [tenantsWithAccess, setTenantsWithAccess] = useState(0);
  const [preSelectedClients, setPreSelectedClients] = useState([]); // Clients that already have access

  const [selected, setSelected] = useState([]);

  const handleSelectAll = () => {
    const availableTenants = tenants.filter(t => !preSelectedClients.includes(t));
    const newlySelected = selected.filter(s => !preSelectedClients.includes(s));
    
    if (newlySelected.length === availableTenants.length) {
      // De-Select All (but keep pre-selected)
      setSelected([...preSelectedClients]);
    } else {
      // Select All (including pre-selected)
      setSelected([...tenants]);
    }
  };

  const handleConfirmSelectTenant = () => {
    setOpenDropdown(false);
  }

  const fetchTenantsWithAccess = async () => {
    try {
      const identifier = document?.file_identifier || (fileIdentifiers && fileIdentifiers.length > 0 ? fileIdentifiers[0] : null);
      if (!identifier) return;
      
      const response = await DocuwareTemplateService.getSharedClientDetails(identifier);
      if (response.data.success) {
        const data = response.data.data;
        const sharedWith = data.sharedWithClients || { view: [], edit: [] };
        const allSharedClients = [...(sharedWith.view || []), ...(sharedWith.edit || [])];
        
        const count = allSharedClients.length;
        setTenantsWithAccess(count);
        setPreSelectedClients(allSharedClients);
        
        // Set selected to include pre-selected clients
        setSelected(prevSelected => {
          const uniqueSelected = [...new Set([...allSharedClients, ...prevSelected])];
          return uniqueSelected;
        });
      }
    } catch (error) {
      console.error("Error fetching tenants with access:", error);
    }
  };

  const handleManageAccessOpen = () => {
    setManageAccessOpen(true);
  };

  const handleManageAccessClose = () => {
    setManageAccessOpen(false);
    // Refresh the count when coming back from manage access modal
    fetchTenantsWithAccess();
  };

  const handleShare = () => {
    // Only share newly selected clients (exclude pre-selected ones)
    const newlySelectedClients = selected.filter(client => !preSelectedClients.includes(client));
    
    if (newlySelectedClients.length === 0) {
      notify({
        type: "info",
        message: "No new clients selected to share."
      });
      return;
    }
    
    setBackdrop(true);
    if (fileIdentifiers.length==0){
        DocuwareTemplateService.shareFiles([document.file_identifier], newlySelectedClients).then(response => {
            setBackdrop(false);
            notify({
                type: "success",
                message: `Document shared successfully with ${newlySelectedClients.length} new client(s).`
              });
            // Refresh the tenants with access count
            fetchTenantsWithAccess();
            setSelected([]);
            onClose();
        }).catch(error => {
            setBackdrop(false);
            notify({
                type: "error",
                message: `Error while sharing the file.`
              });   
        });
    }
    else{
        DocuwareTemplateService.shareFiles(fileIdentifiers, newlySelectedClients).then(response => {
            setBackdrop(false);
            notify({
                type: "success",
                message: `Document shared successfully with ${newlySelectedClients.length} new client(s).`
              });
            // Refresh the tenants with access count
            fetchTenantsWithAccess();
            setSelected([]);
            onClose();
        }).catch(error => {
            setBackdrop(false);
            notify({
                type: "error",
                message: `Error while sharing the file.`
              });   
        });
    }
    
  }

  useEffect(() => {
    if (open) {
      DocuwareTemplateService.getClients().then(response => {
        const filteredClients = response.data.filter(client => client !== authUtil.getClientInfo());
        setTenants(filteredClients);
      }).catch(error => {
          notify({
              type: "error",
              message: `Error while getting the list of tenants.`
          });
      });

      // Fetch tenants with access count
      fetchTenantsWithAccess();
    } else {
      // Reset states when modal closes
      setSelected([]);
      setPreSelectedClients([]);
    }
  },[open, document, fileIdentifiers])

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      classes={{ paper: classes.drawerPaper }}
    >
      <Backdrop open={backdrop} style={{ color: "primary", zIndex: 1000000 }} >
        <CircularProgress color="inherit" />
      </Backdrop>
      {/* Title Bar */}
      <Box className={classes.titleBar}>
        <Box display="flex" alignItems="center" gap={8}>
          <ShareIcon style={{ marginRight: "10px", color: '#0E4B5B' }} />
          <Box>
            <Typography variant="h6"><b>Share Documents</b></Typography>
            {fileIdentifiers.length>0 && <Typography variant="body2" className={classes.section}>
                {fileIdentifiers.length + "files"}
            </Typography> }
            {fileIdentifiers.length==0 &&  <Typography variant="body2" className={classes.section}>
                {document?.name || "No document selected"}
            </Typography>}
          </Box>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      {/* Content */}
      <Box className={classes.content}>
       

      <Autocomplete
        multiple
        disableCloseOnSelect
        options={tenants}
        value={selected}
        onChange={(e, newValue) => {
          // Ensure pre-selected clients cannot be deselected
          const finalValue = [...new Set([...preSelectedClients, ...newValue])];
          setSelected(finalValue);
        }}
        popupIcon={null} // hide default arrow, you can add custom
        open={openDropdown}
        onOpen={() => setOpenDropdown(true)}
        onClose={() => setOpenDropdown(false)}
        getOptionDisabled={(option) => preSelectedClients.includes(option)}
        renderOption={(option, { selected }) => {
          const isPreSelected = preSelectedClients.includes(option);
          return (
            <Box className={classes.optionRow} style={{ opacity: isPreSelected ? 0.6 : 1 }}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                style={{ marginRight: 8 }}
                checked={selected}
                disabled={isPreSelected}
              />
              {option}
              {isPreSelected && (
                <Typography variant="caption" style={{ marginLeft: 'auto', color: '#666', fontSize: '11px' }}>
                  (Already shared)
                </Typography>
              )}
            </Box>
          );
        }}
        renderTags={() => null} // don’t show tags inside input
        classes={{ paper: classes.dropdownPaper }}
        renderInput={(params) => (
            <TextField
            {...params}
            variant="outlined"
            placeholder="Select tenants"
            size="small"
            onClick={() => setOpenDropdown(true)} 
            InputProps={{
                ...params.InputProps,
                style: { height: 48, borderRadius: 8, background: "#fff" },
            }}
            />
        )}
        PaperComponent={({ children }) => (
            <div className={classes.dropdownPaper}>
            {/* Select All / De-select All */}
            <Box
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleSelectAll}
                style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                padding: "6px 12px",
                fontWeight: 600,
                borderBottom: "1px solid #eee",
                background: "#fff",
                }}
            >
                <Checkbox
                indeterminate={
                    selected.length > 0 && selected.length < tenants.length
                }
                checked={selected.length === tenants.length}
                style={{ marginRight: 8 }}
                />
                {selected.length === tenants.length ? "De-Select All" : "Select All"}
            </Box>

            {/* List */}
            {children}

            {/* Footer */}
            <Box className={classes.footer}>
                <Typography variant="body2">
                Tenants Selected{" "}
                <span className={classes.countBadge}>{selected.length}</span>
                </Typography>
                <Button color="primary" size="small" onMouseDown={(e) => e.preventDefault()} onClick={handleConfirmSelectTenant}>
                Confirm Selection
                </Button>
            </Box>
            </div>
        )}
        />

        {selected.length > 0 && (
        <Box
            mt={2}
            p={1}
            style={{
            border: "1px solid #ddd",
            borderRadius: 6,
            background: "#F0F9FF",
            }}
        >
            <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
            >
            <Typography variant="body2">
                Tenants Selected <b>{selected.length}</b>
            </Typography>
            <Button
                size="small"
                color="primary"
                onClick={() => setSelected([])}
            >
                Clear Selection
            </Button>
            </Box>

            {/* Chips */}
            <Box display="flex" flexWrap="wrap" gap={1}>
            {selected.map((tenant) => {
              const isPreSelected = preSelectedClients.includes(tenant);
              return (
                <Box
                key={tenant}
                style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "4px 8px",
                    background: isPreSelected ? "#E8F5E9" : "#fff",
                    border: isPreSelected ? "1px solid #81C784" : "1px solid #ccc",
                    borderRadius: 16,
                    opacity: isPreSelected ? 0.7 : 1,
                }}
                >
                <Typography variant="body2">{tenant}</Typography>
                {!isPreSelected && (
                  <IconButton
                      size="small"
                      style={{ marginLeft: 4 }}
                      onClick={() =>
                      setSelected((prev) => prev.filter((t) => t !== tenant))
                      }
                  >
                      ✕
                  </IconButton>
                )}
                </Box>
              );
            })}
            </Box>
        </Box>
        )}

        <div style={{display:'flex',justifyContent: "space-between", alignItems: "center", marginTop: "20px" }}>
        <Typography variant="body2" className={classes.section}>
          Tenants with access: <b>{tenantsWithAccess}</b>{" "}
          
        </Typography>
        <Button color="primary" size="small" onClick={handleManageAccessOpen}>
            Manage Access →
          </Button>
        </div>
      </Box>

      {/* Footer */}
      <Box className={classes.footer1}>
        <Button 
          onClick={handleShare} 
          className={classes.shareBtn} 
          disabled={selected.filter(client => !preSelectedClients.includes(client)).length === 0}
        >
          Share
        </Button>
      </Box>

      {/* Manage Access Modal */}
      <ManageAccessModal 
        open={manageAccessOpen} 
        onClose={handleManageAccessClose} 
        document={document}
        fileIdentifiers={fileIdentifiers}
      />
    </Drawer>
  );
}
